import { AuthResponseDto } from "@/generated-sources/anagrafica-api";
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";

type Tokens = { accessToken?: string | null; idToken?: string | null; refreshToken?: string | null };

export class AuthHttp {
  axios: AxiosInstance;
  isRefreshing = false;
  refreshPromise: Promise<any> | null = null;
  getTokens: () => Promise<Tokens>;
  originalGetTokens?: () => Promise<Tokens>;
  latestTokens?: Tokens | null = null;
  getUser: () => any;
  commitTokens: (payload: any) => void;
  onRefreshSuccess?: () => void;
  options: { refreshPath: string; refreshTimeoutMs: number; onRefreshFail?: (err: any) => void };

  constructor(
    axiosInstance: AxiosInstance,
    getTokens: () => Promise<Tokens>,
    getUser: () => any,
    commitTokens: (payload: any) => void,
    options?: Partial<{ refreshPath: string; refreshTimeoutMs: number; onRefreshFail: (err: any) => void; onRefreshSuccess: () => void }>
  ) {
    this.axios = axiosInstance;
    this.getTokens = getTokens;
    this.originalGetTokens = getTokens;
    this.getUser = getUser;
    this.commitTokens = commitTokens;
    this.onRefreshSuccess = options && options.onRefreshSuccess;
    this.options = {
      refreshPath: (options && options.refreshPath) || "/v2/auth/refreshToken",
      refreshTimeoutMs: (options && options.refreshTimeoutMs) || 30000,
      onRefreshFail: options && options.onRefreshFail,
    };
  }

  initInterceptors() {
    this.axios.interceptors.response.use(
      (response: AxiosResponse) => {
        return this.parseResponse(response);
      },
      (error) => this.handleResponseError(error)
    );
  }

  tokenIdDetection(AuthorizationHeader: string | undefined, oldTokens: Tokens | null | undefined, newTokens: Tokens) {
    const current = AuthorizationHeader ? AuthorizationHeader.replace("Bearer ", "") : '';
    const oldIdToken = oldTokens?.idToken || '';
    const newIdToken = newTokens.idToken || '';

    // Se il token nell'header corrisponde al vecchio idToken, aggiorna con il nuovo
    if (current && oldIdToken && current === oldIdToken && newIdToken) {
      return `Bearer ${newIdToken}`;
    }
    if (current && oldTokens?.accessToken && current === oldTokens.accessToken && newTokens.accessToken) {
      return `Bearer ${newTokens.accessToken}`;
    }
    // Altrimenti lascia com'è (potrebbe essere un token di un altro utente o servizio)
    return AuthorizationHeader;
  }

  parseResponse(response: AxiosResponse) {
    if (response && response.headers && response.headers["x-page"]) {
      const total = parseInt(response.headers["x-total"] || "0");
      const pagination = {
        total,
        page: parseInt(response.headers["x-page"] || "0"),
        count: parseInt(response.headers["x-count"] || "0"),
        limit: parseInt(response.headers["x-limit"] || "0"),
        from: parseInt(response.headers["x-from"] || "0"),
        to: parseInt(response.headers["x-to"] || "0"),
        totalPages: total > (parseInt(response.headers["x-limit"] || "0") ) ? Math.floor(total / parseInt(response.headers["x-limit"] || "1")) + 1 : 1,
      } as any;
      let data = response.data;
      if (!Array.isArray(data)) {
        const keys = Object.keys(response.data || {});
        if (keys.length == 1) data = response.data[keys[0]];
      }
      response.data = {
        records: data,
        pagination: pagination,
      };
    }
    return response;
  }

  async handleResponseError(error: any) {
    const originalRequest: AxiosRequestConfig & { _retry?: boolean; _isRefresh?: boolean } = error?.config || {};
    const status = error?.response?.status;

    // Avoid handling refresh endpoint or refresh requests to prevent loops
    const url = (originalRequest && (originalRequest.url || "")) as string;
    if ((originalRequest && (originalRequest as any)._isRefresh) || (url && url.includes(this.options.refreshPath))) {
      return Promise.reject(error);
    }

    // Evita loop: se è stato appena fatto un refresh (entro 5 secondi), non rifarlo
    const lastRefreshTimestamp = sessionStorage.getItem('wesmash_token_just_refreshed');
    if (lastRefreshTimestamp) {
      const timeSinceRefresh = Date.now() - parseInt(lastRefreshTimestamp);
      if (timeSinceRefresh < 5000) {
        console.log('[AuthHttp] Token refreshato da poco, skippo nuovo refresh per evitare loop');
        return Promise.reject(error);
      }
    }

    if (originalRequest && status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Se già in corso un refresh, aspetta che finisca
      if (this.isRefreshing && this.refreshPromise) {
        console.log('[AuthHttp] Refresh già in corso, metto in coda la richiesta');
        try {
          await this.refreshPromise;
          // Dopo il refresh, ritenta la richiesta originale
          return this.axios(originalRequest);
        } catch (refreshError) {
          return Promise.reject(refreshError);
        }
      }

      this.isRefreshing = true;

      // Crea una promise per il refresh che altri possono attendere
      this.refreshPromise = (async () => {
        try {
          // use latestTokens if available to avoid reading persisted state mid-update
          const currentTokens = this.latestTokens ? this.latestTokens : await (this.originalGetTokens ? this.originalGetTokens() : this.getTokens());
          const refreshToken = currentTokens?.refreshToken;
          if (!refreshToken) {
            this.isRefreshing = false;
            this.refreshPromise = null;
            if (this.options.onRefreshFail) this.options.onRefreshFail(new Error("No refresh token available"));
            throw error;
          }

          const username = (this.getUser && (this.getUser()?.username || this.getUser()?.email)) || undefined;
          const refreshed = await this.doRefresh(refreshToken, username);

          // Update latestTokens cache first (non-reactive)
          this.latestTokens = refreshed as Tokens;

          // Update Vuex store synchronously
          this.commitTokens(refreshed);

          this.isRefreshing = false;
          this.refreshPromise = null;

          // Notifica successo refresh (può mostrare toast o ricaricare pagina)
          if (this.onRefreshSuccess) {
            this.onRefreshSuccess();
          }

          originalRequest.headers.Authorization = this.tokenIdDetection(originalRequest.headers.Authorization, currentTokens, refreshed)

          // Retry the original request with new tokens from store
          // Non usiamo il token appena ricevuto, forziamo la lettura dallo storeù
          return this.axios(originalRequest);
        } catch (refreshError) {
          this.isRefreshing = false;
          this.refreshPromise = null;
          if (this.options.onRefreshFail) this.options.onRefreshFail(refreshError);
          throw refreshError;
        }
      })();

      return this.refreshPromise;
    }
    return Promise.reject(error);
  }

  async doRefresh(refreshToken: string, username?: string) {
    const path = this.options.refreshPath;
    const configs = {
      headers: {
        Authorization: `Bearer ${refreshToken}`,
      },
    };

    // const refreshAxios = axios.create({ baseURL: this.axios.defaults.baseURL });
    // don't attach interceptors to refreshAxios

    const requestPromise = axios.post<AuthResponseDto>(path, { username }, configs).then((r) => r.data);
    // mark as refresh request for safety if request passes through shared axios
    (requestPromise as any)._isRefresh = true;

    // const timeoutPromise = new Promise((_, reject) => {
    //   setTimeout(() => reject(new Error("Refresh token request timed out")), this.options.refreshTimeoutMs);
    // });

    return requestPromise;
  }
}
